import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef
} from 'react';
import { useLocation } from 'react-router';

import useSocket from '@/hooks/useSocket';
import {
  getDateWithTimeZone,
  setFormatDateToView
} from '@/solaris-lens-common-lib/src/utils/dates';

type EntityData = {
  entityName: string;
  entityId: string;
};

type WebSocketContextType = {
  lastUpdateTime: string;
  readyState: number;
  setEntityData: (entityData: EntityData) => void;
  socketData: any;
};

const WebSocketContext = createContext<WebSocketContextType>({
  lastUpdateTime: '',
  readyState: 0,
  setEntityData: () => {},
  socketData: null
});

export const WebSocketProvider: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  const initialDateTime = setFormatDateToView(
    getDateWithTimeZone({
      date: new Date(),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    })
  );
  const location = useLocation();
  const previousPathRef = useRef<string>(location.pathname);
  const [connectWs, setConnectWs] = useState(false);
  const [lastUpdateTime, setLastUpdateTime] = useState<string>(initialDateTime);
  const [socketData, setSocketData] = useState<any>(null);

  const [entityData, setEntityData] = useState<EntityData>({
    entityName: '',
    entityId: ''
  });

  const shouldConnect =
    entityData.entityName !== '' && entityData.entityId !== '';

  useEffect(() => {
    setConnectWs(shouldConnect);

    if (!shouldConnect) {
      setSocketData(null);
      setLastUpdateTime(initialDateTime);
    }
  }, [shouldConnect]);

  useEffect(() => {
    const currentPath = location.pathname;
    const previousPath = previousPathRef.current;

    if (previousPath !== currentPath) {
      const isTurbineDetailRoute = /^\/turbines\/[^/]+/.test(currentPath);
      const isLocationDetailRoute = /^\/locations\/[^/]+/.test(currentPath);
      const wasTurbineDetailRoute = /^\/turbines\/[^/]+/.test(previousPath);
      const wasLocationDetailRoute = /^\/locations\/[^/]+/.test(previousPath);

      const isTabChangeOnly =
        (isTurbineDetailRoute && wasTurbineDetailRoute) ||
        (isLocationDetailRoute && wasLocationDetailRoute);

      if (!isTabChangeOnly) {
        setConnectWs(false);
        setSocketData(null);
        setLastUpdateTime(initialDateTime);
      }

      previousPathRef.current = currentPath;
    }
  }, [location.pathname]);

  const onLastWsMessage = (socketResponse: any) => {
    setLastUpdateTime(
      setFormatDateToView(getDateWithTimeZone(socketResponse?._meta?.sentAt))
    );
    setSocketData(socketResponse?.data);
  };

  const { readyState } = useSocket({
    connectWs,
    entityName: entityData.entityName,
    entityId: entityData.entityId,
    onLastWsMessage
  });

  return (
    <WebSocketContext.Provider
      value={{
        lastUpdateTime,
        readyState,
        setEntityData,
        socketData
      }}
    >
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocket = () => useContext(WebSocketContext);
